import { AHText, Box, OrgIcon, useLayoutContext, useScaleFactor } from "arkh";
import React from "react";
import { Image } from "react-native";
import { graphql, useFragment } from "react-relay";
import { WidgetTeamScheduleRoutineListItemContent_Routine$key } from "./__generated__/WidgetTeamScheduleRoutineListItemContent_Routine.graphql";
import { WidgetTeamScheduleTimeRangeIndicator } from "./WidgetTeamScheduleTimeRangeIndicator";
import { WidgetTeamScheduleRoutineListItemContent_RoutineOccasion$key } from "./__generated__/WidgetTeamScheduleRoutineListItemContent_RoutineOccasion.graphql";

import { notEmpty } from "../utils";
import { ToolbarButtonAssignees } from "../Todo";
import { ListItemAssignees } from "../TodoListItem/TodoListItemFooter/ListItemAssignees";

export function WidgetTeamScheduleRoutineListItemContent(props: {
  routine: WidgetTeamScheduleRoutineListItemContent_Routine$key;
  routineOccasion: WidgetTeamScheduleRoutineListItemContent_RoutineOccasion$key;
  status: "active" | "soon" | "none";
  size: "COMPACT" | "DEFAULT" | "LARGE";
  freq: "DAILY" | "WEEKLY" | "MONTHLY";
  onAssigneesPress?: () => void;
}) {
  const layout = useLayoutContext();
  const sf = useScaleFactor();
  const routineOccasion = useFragment(
    graphql`
      fragment WidgetTeamScheduleRoutineListItemContent_RoutineOccasion on RoutineOccasion {
        startAt
        deadlineAt
        assigned {
          id
          employee {
            id
            name
            isLinkedToViewer
            ...Avatar_employee
            ...ToolbarButtonAssignees_employees
            ...ListItemAssignees_employees
          }
        }
        assignedRoles {
          role {
            id
            name
            isLinkedToViewer
            employeeIds
            ...ToolbarButtonAssignees_roles
            ...ListItemAssignees_roles
          }
        }
      }
    `,
    props.routineOccasion
  );

  const routine = useFragment(
    graphql`
      fragment WidgetTeamScheduleRoutineListItemContent_Routine on Routine {
        id
        description
        title
        isOrg
        orgIconUri
        descriptionImages {
          size(minHeight: 120, minWidth: 120) {
            url
          }
        }
      }
    `,
    props.routine
  );

  const imageSize =
    props.status === "active" && props.size !== "COMPACT" ? 60 : 30;
  const descriptionString = routine.description?.trim() ?? "";
  const showDescriptionAndImages =
    (props.status === "active" || props.status === "soon") &&
    ((routine.descriptionImages ?? []).length > 0 ||
      (descriptionString?.length ?? 0) > 0);

  const assignees =
    routineOccasion.assigned?.map((e) => e.employee).filter(notEmpty) ?? [];
  const assignedRoles = routineOccasion.assignedRoles?.map((r) => r.role).filter(notEmpty) ?? [];

  return (
    <Box style={{ flex: 1, justifyContent: "center", alignItems: "stretch" }}>
      {props.freq === "MONTHLY" || props.freq === "WEEKLY" ? (
        <WidgetTeamScheduleTimeRangeIndicator
          deadlineAt={routineOccasion.deadlineAt}
          startAt={routineOccasion.startAt}
          freq={props.freq}
        />
      ) : null}
      <Box style={{ flexDirection: "row", alignItems: "center" }}>
        {routine.isOrg ? (
          <OrgIcon orgIconUri={routine.orgIconUri} spaceRight="xsmall" />
        ) : null}
        <AHText
          type="title-small"
          adjustsFontSizeToFit
          minimumFontScale={0.8}
          numberOfLines={1}
          color="darkerText"
          spaceRight="medium"
          ellipsizeMode="tail"
          style={{ flex: 1 }}
        >
          {routine.title ?? ""}
        </AHText>
      </Box>
      {showDescriptionAndImages ? (
        <Box
          style={{ flexDirection: "row", alignItems: "flex-start" }}
          spaceTop={
            props.status === "active" && props.size !== "COMPACT"
              ? "xsmall"
              : "xxsmall"
          }
        >
          {(routine.descriptionImages ?? []).length > 0 ? (
            <Image
              source={{
                uri: routine.descriptionImages?.[0]?.size?.url ?? "",
              }}
              style={{
                width: imageSize * sf,
                height: imageSize * sf,
                borderRadius: layout.radius.small * sf,
                marginRight:
                  (props.status === "active"
                    ? layout.space.small
                    : layout.space.xsmall) * sf,
              }}
            />
          ) : null}
          {(descriptionString?.length ?? 0) > 0 ? (
            <AHText
              style={{ flex: 1, lineHeight: 15 * sf }}
              numberOfLines={
                props.status === "active" && props.size !== "COMPACT"
                  ? props.size === "LARGE"
                    ? 11
                    : 5
                  : 2
              }
              spaceRight="xxsmall"
            >
              {props.status === "active"
                ? descriptionString
                : descriptionString.replace(/\n/g, "")}
            </AHText>
          ) : null}
        </Box>
      ) : null}
      {props.onAssigneesPress ? (
        <Box style={{ flexDirection: "row" }}>
          <ToolbarButtonAssignees
            assignedEmployees={assignees}
            assignedRoles={assignedRoles}
            isLocked={false}
            onPress={() => { props.onAssigneesPress?.(); }}
          />
        </Box>
      ) : (
        <ListItemAssignees
          employees={assignees}
          roles={assignedRoles}
          currentEmployeeId={undefined}
          textType="detail"
        />
      )}
    </Box>
  );
}
